/**
 * Custom Hook for Header Notifications
 * 
 * Manages notification state, fetching, and actions (mark as read, delete, clear).
 */

import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/app/providers/ToastContext.jsx';
import { notificationService } from '@/shared/services';
import { logger } from '@/shared/utils/logger.js';

/**
 * Format notification timestamp
 */
const formatNotificationTimestamp = (value) => {
  if (!value) {return '';}
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {return '';}
  return date.toLocaleString();
};

/**
 * Normalize notification data from API
 */
const normalizeNotification = (item, index) => {
  if (!item) {return null;}
  const timestamp = item.time || item.created_at || item.updated_at || item.timestamp || null;
  return {
    id: item.id || `notification-${index}`,
    message: item.messagedetails || item.message || item.title || '',
    code: item.responsecode || item.code || '',
    isRead: Boolean(item.is_read ?? item.isRead ?? false),
    timestamp,
    formattedTime: formatNotificationTimestamp(timestamp),
  };
};

export const useHeaderNotifications = (isOpen) => {
  const { t } = useTranslation();
  const { error: pushToastError } = useToast();
  const [notifications, setNotifications] = useState([]);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);
  const [notificationError, setNotificationError] = useState(null);
  const [actioningNotificationId, setActioningNotificationId] = useState(null);
  const [notificationActionType, setNotificationActionType] = useState(null);
  const [isClearingNotifications, setIsClearingNotifications] = useState(false);
  const notificationsFetchedRef = useRef(false);

  const fetchNotifications = useCallback(async () => {
    setIsLoadingNotifications(true);
    setNotificationError(null);
    try {
      const response = await notificationService.getNotifications();
      const payload = response?.data ?? response ?? {};
      const source = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload?.notifications)
            ? payload.notifications
            : [];
      const normalized = source
        .map((item, index) => normalizeNotification(item, index))
        .filter(Boolean);
      setNotifications(normalized);
      notificationsFetchedRef.current = true;
    } catch (error) {
      const message =
        error?.message ||
        t('notificationLoadError', {
          ns: 'common',
          defaultValue: 'Failed to load notifications.',
        });
      setNotificationError(message);
      pushToastError(message);
      logger.error('Failed to fetch notifications', { error });
    } finally {
      setIsLoadingNotifications(false);
    }
  }, [pushToastError, t]);

  const handleRefreshNotifications = useCallback(() => {
    notificationsFetchedRef.current = false;
    fetchNotifications();
  }, [fetchNotifications]);

  const handleMarkNotificationRead = useCallback(
    async (notificationId) => {
      if (!notificationId) {return;}
      setActioningNotificationId(notificationId);
      setNotificationActionType('mark');
      try {
        await notificationService.markAsRead(notificationId);
        setNotifications((prev) =>
          prev.map((notification) =>
            notification.id === notificationId ? { ...notification, isRead: true } : notification
          )
        );
      } catch (error) {
        const message =
          error?.message ||
          t('notificationMarkReadError', {
            ns: 'common',
            defaultValue: 'Could not mark notification as read.',
          });
        pushToastError(message);
        logger.error('Failed to mark notification as read', { error, notificationId });
      } finally {
        setActioningNotificationId(null);
        setNotificationActionType(null);
      }
    },
    [pushToastError, t]
  );

  const handleDeleteNotification = useCallback(
    async (notificationId) => {
      if (!notificationId) {return;}
      setActioningNotificationId(notificationId);
      setNotificationActionType('delete');
      try {
        await notificationService.deleteNotification(notificationId);
        setNotifications((prev) => prev.filter((notification) => notification.id !== notificationId));
      } catch (error) {
        const message =
          error?.message ||
          t('notificationDeleteError', {
            ns: 'common',
            defaultValue: 'Could not delete notification.',
          });
        pushToastError(message);
        logger.error('Failed to delete notification', { error, notificationId });
      } finally {
        setActioningNotificationId(null);
        setNotificationActionType(null);
      }
    },
    [pushToastError, t]
  );

  const handleClearNotifications = useCallback(async () => {
    if (!notifications.length) {return;}
    setIsClearingNotifications(true);
    try {
      await notificationService.deleteAllNotifications();
      setNotifications([]);
    } catch (error) {
      const message =
        error?.message ||
        t('notificationClearError', {
          ns: 'common',
          defaultValue: 'Could not clear notifications.',
        });
      pushToastError(message);
      logger.error('Failed to clear notifications', { error });
    } finally {
      setIsClearingNotifications(false);
      setNotificationActionType(null);
    }
  }, [notifications.length, pushToastError, t]);

  const notificationsUnreadCount = useMemo(
    () => notifications.filter((notification) => !notification.isRead).length,
    [notifications]
  );

  const displayNotificationCount = notificationsUnreadCount;

  const sortedNotifications = useMemo(() => {
    return [...notifications].sort((a, b) => {
      const timeA = a.timestamp ? Date.parse(a.timestamp) : 0;
      const timeB = b.timestamp ? Date.parse(b.timestamp) : 0;
      return timeB - timeA;
    });
  }, [notifications]);

  // Fetch notifications on mount and when dropdown opens (but only once per session)
  useEffect(() => {
    // Only fetch if dropdown is open and we haven't fetched yet
    if (isOpen && !notificationsFetchedRef.current && !isLoadingNotifications) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications, isLoadingNotifications]);

  return {
    notifications: sortedNotifications,
    isLoadingNotifications,
    notificationError,
    notificationsUnreadCount: displayNotificationCount,
    actioningNotificationId,
    notificationActionType,
    isClearingNotifications,
    fetchNotifications,
    handleRefreshNotifications,
    handleMarkNotificationRead,
    handleDeleteNotification,
    handleClearNotifications,
  };
};

